/// <reference path="../_references.ts"/>

module App.User {
	import LoginService = App.Login.LoginService;
	import AppointmentService = App.Appointments.AppointmentService;
	import TimeclockService = App.Timeclock.TimeclockService;
	import SelectedAppointment = App.Appointments.SelectedAppointment;

	interface LocalStorage extends ng.local.storage.ILocalStorageService {
		uId: number;
		userData: App.Login.userLoginData;
		$reset: () => void;
	}

	interface Tech {
		id: number;
		latest_time: App.Timeclock.Timeclock;
		full_name: string;
	}

	export interface User {
		id: number;
		email: string;
		last_online_date: null;
		avatar: string;
		username: string;
		favorites: number[];
		technician?: Tech;
		permissions: string[];
		role: App.Login.roleLoginData|null;
		tech_role: App.Login.roleLoginData|null;
		first_name?: string;
		last_name?: string;
		full_name?: string;
		organization?: { id: number, name: string };
	}

	export interface UserSession {
		user: User;
		getUser: () => ng.IPromise<User>;
		setUser: (user: User) => void;
		checkSession: () => void;
		destroySession: () => ng.IPromise<{}>;
		fetchSessionData: () => ng.IPromise<void>;
		pauseAllAppointments: () => ng.IPromise<{}>;
		clockInTimeclock: () => ng.IPromise<any>;
		clockOutTimeclock: () => ng.IPromise<any>;
		isClocked: () => boolean;
		fetchUserInfo: () => ng.IPromise<void>;
		addFavorite: (id: number) => void;
		removeFavorite: (id: number) => void;
	}

	export function UserSessionFactory(
		$window: ng.IWindowService,
		$http: ng.IHttpService,
		$localStorage: LocalStorage,
		$q: ng.IQService,
		Constants: IConstants,
		LoginService: LoginService,
		AppointmentService: AppointmentService,
		TimeclockService: TimeclockService,
		SelectedAppointment: SelectedAppointment,
		$location: ng.ILocationService,
		$stateParams: ng.ui.IStateParamsService
	): UserSession {
		let isFetching: boolean = false,
			fetchCallbacks: ng.IDeferred<void>[] = [],
			User: UserSession = {
				user: null,
				getUser: () => {
					if (User.user)
						return $q.when(User.user);
					return User.fetchSessionData().then(() => {
						return User.user;
					});
				},
				setUser: (user?: User) => {
					if ((user && user.technician) || !!$location.search().office) {
						User.user = user;

						if(!$window.userlaneSettings && !$window.debugInfoEnabled) {
							let tags = [
								'technician'
							];

							if (user.organization) {
								tags.push(`organization${user.organization.id}`);
							}

							if (user.role) {
								tags.push(`role${user.role.id}`);
							}

							$window.userlaneSettings = {
								app_id: 32112,
								user: {
									id: user.id,
									tags: tags
								}
							};

							let userlaneTag = document.createElement('script');
							userlaneTag.type = 'text/javascript';
							userlaneTag.src = '//cdn.userlane.com/userlane.js';
							document.body.appendChild(userlaneTag);
						}
					} else {
						LoginService.destroySession();
						$window.location.href = '/';
					}
				},
				checkSession: () => {
					if(!LoginService.isActiveSession() && $window.location.pathname !== '/') {
						$window.location.href = '/?redirect=' + encodeURIComponent($window.location.pathname);
					} else if(LoginService.isActiveSession() && $window.location.pathname === '/') {
						$window.location.href = $stateParams.redirect
							? decodeURIComponent($stateParams.redirect)
							: '/home/appointment';
					} else if(LoginService.isActiveSession()) {
						User.fetchSessionData();
					}
				},
				destroySession: () => {
					return LoginService.logoutSession();
				},
				fetchSessionData: () => {
					if (isFetching) {
						let defer: ng.IDeferred<any> = $q.defer();
						fetchCallbacks.push(defer);
						return defer.promise;
					}

					isFetching = true;
					return User.fetchUserInfo().then(() => {
						if (!User.user) {
							return;
						}

						const prevId = $localStorage.uId,
							curId = User.user.id;

						if (prevId != curId && prevId) {
							$localStorage.$reset();
						}

						$localStorage.uId = curId;

						User.user.favorites = User.user.favorites || [];
						$localStorage.userData = User.user;

						while (fetchCallbacks.length > 0) {
							fetchCallbacks[0].resolve();
							fetchCallbacks.shift();
						}

						isFetching = false;
					}).catch(() => { isFetching = false; });
				},
				pauseAllAppointments: () => {
					return callWithUserId(AppointmentService.pauseAll.bind(AppointmentService));
				},
				clockInTimeclock: () => {
					return callWithUserId(TimeclockService.clockIn.bind(TimeclockService))
						.then(() => { return User.fetchSessionData(); });
				},
				clockOutTimeclock: () => {
					return callWithUserId(TimeclockService.clockOut.bind(TimeclockService))
						.then(() => { return User.fetchSessionData(); });
				},
				isClocked: () => {
					return User.user &&
						User.user.technician &&
						User.user.technician.latest_time &&
						User.user.technician.latest_time.active;
				},
				fetchUserInfo: () => {
					return $http.get(`${Constants.apiUrl}user/current`).then((response) => {
						User.setUser(<User>response.data);
					}, (response) => {
						if(response.status === 401) {
							LoginService.destroySession();
							$window.location.href = '/';
						}
					});
				},
				addFavorite: (id) => {
					User.user.favorites.push(id);

					updateUserFavorites(id);
				},
				removeFavorite: (id) => {
					User.user.favorites.splice(User.user.favorites.indexOf(id), 1);

					updateUserFavorites(id);
				}
			};

		function callWithUserId(call: (id: number) => ng.IPromise<any>): ng.IPromise<any> {
			if (User.user && User.user.id)
				return call(User.user.id);
			return User.fetchSessionData().then(() => {
				return call(User.user.id);
			});
		}
		function updateUserFavorites(id: number) {
			return $http.put(`${Constants.apiUrl}user/${User.user.id}`, {
				favorites: User.user.favorites
			}).catch(() => {
				User.user.favorites.splice(User.user.favorites.indexOf(id), 1);
			});
		}

		return User;
	}

	UserSessionFactory.$inject = ['$window', '$http', '$localStorage', '$q', 'Constants', 'LoginService', 'AppointmentService', 'TimeclockService', 'SelectedAppointment', '$location', '$stateParams'];
}
